import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { getEventTypeDescription, getEventTypeLabel } from "../shared/utils";
import { LEAD_EVENT_TYPES, type LeadEventType } from "../shared/types";
import type { SalesforceEventMapping, SalesforceField } from "./types";
import { VariableHelpTooltip } from "../shared/VariableHelpTooltip";

type ProjectWithSalesforceConfig = {
  id: string;
  name: string;
  crmConfiguration?: {
    id: string;
    salesforceEventsMapping: SalesforceEventMapping[];
    isFallback?: boolean;
  };
};

const SalesforceCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const { data: projects, isLoading: isLoadingProjects } = trpc.crmSelfConfig.listProjects.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && merchantType === "development" }
  );

  const {
    data: crmConfigurations,
    isLoading: isLoadingConfigurations,
    refetch: refetchConfigurations,
  } = trpc.crmSelfConfig.listCrmConfigurations.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const salesforceConfigurations = useMemo(
    () => (crmConfigurations || []).filter((config) => config.provider === "salesforce"),
    [crmConfigurations]
  );

  const fallbackConfiguration = useMemo(
    () => salesforceConfigurations.find((config) => config.isFallback),
    [salesforceConfigurations]
  );

  const projectsWithConfig = useMemo<ProjectWithSalesforceConfig[]>(() => {
    if (merchantType !== "development" || !projects || !crmConfigurations) return [];

    return projects.map((project) => {
      const config =
        salesforceConfigurations.find((c) => c.projectId === project.id) || fallbackConfiguration;

      return {
        id: project.id,
        name: project.name,
        crmConfiguration: config
          ? {
              id: config.id,
              salesforceEventsMapping: (config.salesforceEventsMapping ||
                []) as SalesforceEventMapping[],
              isFallback: !config.projectId && config.isFallback,
            }
          : undefined,
      };
    });
  }, [projects, salesforceConfigurations, fallbackConfiguration, merchantType, crmConfigurations]);

  const [eventMappings, setEventMappings] = useState<SalesforceEventMapping[]>([]);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [objectApiNamesByEvent, setObjectApiNamesByEvent] = useState<Record<string, string>>({});
  const [fieldsByObject, setFieldsByObject] = useState<Record<string, SalesforceField[]>>({});
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
  // Track selected fields and their values by event type: { eventType: { fieldName: fieldValue } }
  const [selectedFieldsByEvent, setSelectedFieldsByEvent] = useState<
    Record<string, Record<string, string>>
  >({});

  const utils = trpc.useUtils();
  const [shouldCloseEditMode, setShouldCloseEditMode] = useState(true);

  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración creada", {
        description: "La configuración de Salesforce se guardó correctamente.",
      });
      if (shouldCloseEditMode) {
        setEditingProjectId(null);
      }
      setShouldCloseEditMode(true); // Reset to default
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la configuración.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración actualizada", {
        description: "La configuración de Salesforce se actualizó correctamente.",
      });
      if (shouldCloseEditMode) {
        setEditingProjectId(null);
      }
      setShouldCloseEditMode(true); // Reset to default
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  const [objectApiNameToDescribe, setObjectApiNameToDescribe] = useState<string | null>(null);

  const describeObject = trpc.crmSelfConfig.getSalesforceObjectDescribe.useMutation({
    onSuccess: (result) => {
      if (!objectApiNameToDescribe) return;

      if (result.success && result.data) {
        // Backend already filters updateable fields
        setFieldsByObject((prev) => ({
          ...prev,
          [objectApiNameToDescribe]: result.data.fields,
        }));
      } else if (result.success === false) {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los campos del objeto.",
        });
      }
      setLoadingFields((prev) => ({
        ...prev,
        [objectApiNameToDescribe]: false,
      }));
      setObjectApiNameToDescribe(null);
    },
    onError: (error) => {
      if (!objectApiNameToDescribe) return;

      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los campos del objeto.",
      });
      setLoadingFields((prev) => ({
        ...prev,
        [objectApiNameToDescribe]: false,
      }));
      setObjectApiNameToDescribe(null);
    },
  });

  // Trigger mutation when objectApiNameToDescribe changes
  useEffect(() => {
    if (!merchantId || !objectApiNameToDescribe || objectApiNameToDescribe.trim() === "") {
      return;
    }

    setLoadingFields((prev) => ({
      ...prev,
      [objectApiNameToDescribe]: true,
    }));

    describeObject.mutate({
      merchantId: merchantId,
      objectApiName: objectApiNameToDescribe,
    });
  }, [objectApiNameToDescribe, merchantId]);

  useEffect(() => {
    if (!editingProjectId) {
      setEventMappings([]);
      setObjectApiNamesByEvent({});
      setSelectedFieldsByEvent({});
      return;
    }

    const config = projectsWithConfig.find(
      (project) => project.id === editingProjectId
    )?.crmConfiguration;

    if (!config?.salesforceEventsMapping) {
      setEventMappings([]);
      setObjectApiNamesByEvent({});
      return;
    }

    const mappings = config.salesforceEventsMapping as SalesforceEventMapping[];
    setEventMappings(mappings);

    // Load objectApiNames and selected fields from existing mappings
    const objectApiNames: Record<string, string> = {};
    const selectedFields: Record<string, Record<string, string>> = {};
    mappings.forEach((mapping) => {
      if (mapping.updateLead?.objectApiName) {
        objectApiNames[mapping.event] = mapping.updateLead.objectApiName;
        // Load selected fields (exclude objectApiName from the fields)
        const { objectApiName, ...fieldMappings } = mapping.updateLead;
        if (Object.keys(fieldMappings).length > 0) {
          selectedFields[mapping.event] = fieldMappings as Record<string, string>;
          console.log(
            `[Salesforce Config] Loaded fields for event ${mapping.event}:`,
            selectedFields[mapping.event]
          );
        }
      }
    });
    setObjectApiNamesByEvent(objectApiNames);
    setSelectedFieldsByEvent(selectedFields);
  }, [editingProjectId, projectsWithConfig]);

  const handleLoadFields = (eventType: LeadEventType) => {
    const objectApiName = objectApiNamesByEvent[eventType];
    if (!objectApiName || objectApiName.trim() === "" || !merchantId) {
      toast.warning("Campo requerido", {
        description: "Por favor ingresa un Object API Name antes de cargar los campos.",
      });
      return;
    }

    if (loadingFields[objectApiName]) {
      return; // Already loading
    }

    setLoadingFields((prev) => ({
      ...prev,
      [objectApiName]: true,
    }));

    setObjectApiNameToDescribe(objectApiName.trim());
  };

  const getAvailableEventTypes = (): LeadEventType[] => {
    const usedEvents = new Set(eventMappings.map((m) => m.event));
    return LEAD_EVENT_TYPES.filter((event) => !usedEvents.has(event));
  };

  const handleAddEventType = (eventType: LeadEventType) => {
    setEventMappings((prev) => [
      ...prev,
      {
        event: eventType,
      },
    ]);
  };

  const handleRemoveEventType = async (eventType: LeadEventType) => {
    if (!merchantId || !crmConnection?.provider || !editingProjectId) {
      // If not in edit mode, just update local state
      setEventMappings((prev) => prev.filter((m) => m.event !== eventType));
      setObjectApiNamesByEvent((prev) => {
        const updated = { ...prev };
        delete updated[eventType];
        return updated;
      });
      setSelectedFieldsByEvent((prev) => {
        const updated = { ...prev };
        delete updated[eventType];
        return updated;
      });
      return;
    }

    // Calculate updated state before applying it
    const updatedMappings = eventMappings.filter((m) => m.event !== eventType);
    const updatedObjectApiNames = { ...objectApiNamesByEvent };
    delete updatedObjectApiNames[eventType];
    const updatedSelectedFields = { ...selectedFieldsByEvent };
    delete updatedSelectedFields[eventType];

    // Update local state
    setEventMappings(updatedMappings);
    setObjectApiNamesByEvent(updatedObjectApiNames);
    setSelectedFieldsByEvent(updatedSelectedFields);

    // Build the salesforceEventsMapping array with the updated mappings
    const salesforceEventsMapping: SalesforceEventMapping[] = [];

    for (const mapping of updatedMappings) {
      if (mapping.updateLead) {
        const objectApiName =
          updatedObjectApiNames[mapping.event] || mapping.updateLead.objectApiName;

        if (objectApiName && objectApiName.trim() !== "") {
          const selectedFields = updatedSelectedFields[mapping.event] || {};
          salesforceEventsMapping.push({
            event: mapping.event,
            updateLead: {
              objectApiName: objectApiName.trim(),
              ...selectedFields,
            },
          });
        } else {
          // If objectApiName is missing, just add the event without updateLead
          salesforceEventsMapping.push({
            event: mapping.event,
          });
        }
      } else {
        salesforceEventsMapping.push({
          event: mapping.event,
        });
      }
    }

    // Save immediately to database
    try {
      // Set flag to prevent closing edit mode
      setShouldCloseEditMode(false);

      const currentConfiguration = projectsWithConfig.find(
        (project) => project.id === editingProjectId
      )?.crmConfiguration;

      if (currentConfiguration && !currentConfiguration.isFallback) {
        await updateConfiguration.mutateAsync({
          merchantId,
          id: currentConfiguration.id,
          salesforceEventsMapping,
        });
      } else {
        await createConfiguration.mutateAsync({
          merchantId,
          provider: "salesforce",
          enabled: true,
          projectId: editingProjectId,
          salesforceEventsMapping,
        });
      }

      toast.success("Evento eliminado", {
        description: "El evento ha sido eliminado de la configuración.",
      });
    } catch (error: any) {
      toast.error("Error al eliminar", {
        description:
          error.message || "No se pudo eliminar el evento. Por favor intenta nuevamente.",
      });
      // Revert local state on error
      setEventMappings(eventMappings);
      setObjectApiNamesByEvent(objectApiNamesByEvent);
      setSelectedFieldsByEvent(selectedFieldsByEvent);
    }
  };

  const handleToggleUpdateLead = (eventType: LeadEventType, enabled: boolean) => {
    setEventMappings((prev) => {
      const existing = prev.find((m) => m.event === eventType);
      if (!existing) return prev;

      if (enabled) {
        return prev.map((m) =>
          m.event === eventType
            ? {
                ...m,
                updateLead: {
                  objectApiName: "",
                },
              }
            : m
        );
      } else {
        return prev.map((m) => {
          if (m.event === eventType) {
            const { updateLead, ...rest } = m;
            return rest;
          }
          return m;
        });
      }
    });

    if (!enabled) {
      setObjectApiNamesByEvent((prev) => {
        const updated = { ...prev };
        delete updated[eventType];
        return updated;
      });
      setSelectedFieldsByEvent((prev) => {
        const updated = { ...prev };
        delete updated[eventType];
        return updated;
      });
    }
  };

  const handleObjectApiNameChange = (eventType: LeadEventType, value: string) => {
    setObjectApiNamesByEvent((prev) => ({
      ...prev,
      [eventType]: value,
    }));

    // Update the mapping with the objectApiName
    setEventMappings((prev) => {
      return prev.map((m) => {
        if (m.event === eventType && m.updateLead) {
          return {
            ...m,
            updateLead: {
              ...m.updateLead,
              objectApiName: value,
            },
          };
        }
        return m;
      });
    });
  };

  const handleToggleFieldSelection = (
    eventType: LeadEventType,
    fieldName: string,
    selected: boolean
  ) => {
    setSelectedFieldsByEvent((prev) => {
      const eventFields = prev[eventType] || {};
      if (selected) {
        return {
          ...prev,
          [eventType]: {
            ...eventFields,
            [fieldName]: "", // Initialize with empty value
          },
        };
      } else {
        const updated = { ...eventFields };
        delete updated[fieldName];
        const newState = { ...prev };
        if (Object.keys(updated).length > 0) {
          newState[eventType] = updated;
        } else {
          delete newState[eventType];
        }
        return newState;
      }
    });
  };

  const handleFieldValueChange = (eventType: LeadEventType, fieldName: string, value: string) => {
    setSelectedFieldsByEvent((prev) => ({
      ...prev,
      [eventType]: {
        ...(prev[eventType] || {}),
        [fieldName]: value,
      },
    }));
  };

  const handleSelectProject = (projectId: string) => {
    setEditingProjectId(projectId);
  };

  const handleSave = async () => {
    if (!merchantId || !crmConnection?.provider || !editingProjectId) {
      if (!editingProjectId) {
        toast.warning("Selecciona un proyecto", {
          description: "Elige un proyecto para configurar Salesforce.",
        });
      }
      return;
    }

    // Set flag to close edit mode after save
    setShouldCloseEditMode(true);

    const salesforceEventsMapping: SalesforceEventMapping[] = [];

    for (const mapping of eventMappings) {
      if (mapping.updateLead) {
        const objectApiName =
          objectApiNamesByEvent[mapping.event] || mapping.updateLead.objectApiName;

        if (!objectApiName || objectApiName.trim() === "") {
          toast.error("Campo requerido", {
            description: `Para el evento "${getEventTypeLabel(
              mapping.event
            )}", el campo "Object API Name" es obligatorio.`,
          });
          return;
        }

        const selectedFields = selectedFieldsByEvent[mapping.event] || {};
        salesforceEventsMapping.push({
          event: mapping.event,
          updateLead: {
            objectApiName: objectApiName.trim(),
            ...selectedFields, // Include selected fields and their values
          },
        });
      } else {
        salesforceEventsMapping.push({
          event: mapping.event,
        });
      }
    }

    const currentConfiguration = projectsWithConfig.find(
      (project) => project.id === editingProjectId
    )?.crmConfiguration;

    if (currentConfiguration && !currentConfiguration.isFallback) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: currentConfiguration.id,
        salesforceEventsMapping,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        provider: "salesforce",
        enabled: true,
        projectId: editingProjectId,
        salesforceEventsMapping,
      });
    }
  };

  if (isLoadingConfigurations) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-600">
            Cargando configuración...
          </p>
        </div>
      </div>
    );
  }

  if (!crmConnection || crmConnection.provider !== "salesforce") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">Conecta Salesforce para configurar los eventos.</p>
      </div>
    );
  }

  if (merchantType !== "development") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">
          La configuración de Salesforce está disponible para comercios de tipo desarrollo.
        </p>
      </div>
    );
  }

  if (isLoadingProjects) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-600">
            Cargando proyectos...
          </p>
        </div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">No hay proyectos disponibles.</p>
      </div>
    );
  }

  const availableEventTypes = getAvailableEventTypes();
  const selectedProject = editingProjectId
    ? projectsWithConfig.find((project) => project.id === editingProjectId)
    : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-lg font-bold">
            Acciones Salesforce
          </p>
          <p className="text-sm text-gray-600">
            Configura cómo Palomma debe actualizar los leads en Salesforce cuando se produzcan
            eventos.
          </p>
        </div>

        <div>
          <p className="mb-1 text-sm font-semibold">
            Configuración por Proyecto
          </p>
          <p className="mb-4 text-xs text-gray-500">
            Selecciona el proyecto para el que deseas configurar el mapeo de eventos.
          </p>
          <div className="flex flex-col gap-3">
            {projectsWithConfig.map((project) => {
              const configuredEvents = project.crmConfiguration?.salesforceEventsMapping || [];
              const isConfigured = configuredEvents.length > 0;
              const isSelected = editingProjectId === project.id;
              const eventsSummary = isConfigured
                ? configuredEvents.map((m) => getEventTypeLabel(m.event)).join(", ")
                : "Sin eventos configurados";
              return (
                <div
                  key={project.id}
                  className={`rounded-md border p-4 ${
                    isSelected ? "border-purple-200 bg-purple-50/25" : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{project.name}</p>
                      <p className="text-xs text-gray-500">
                        {eventsSummary}
                      </p>
                      {project.crmConfiguration?.isFallback && (
                        <p className="text-xs text-orange-500">
                          Configuración heredada (fallback)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          isConfigured
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-yellow-100 text-yellow-800 border-yellow-200"
                        }
                      >
                        {isConfigured ? "Configurado" : "Pendiente"}
                      </Badge>
                      <Button
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        onClick={() => handleSelectProject(project.id)}
                      >
                        {isConfigured ? "Editar" : "Configurar"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {editingProjectId ? (
          <>
            <div>
              <p className="mb-3 text-sm font-semibold">
                Mapeo de eventos · {selectedProject?.name}
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-2 text-xs font-semibold text-gray-600">
                    Tipos de Eventos
                  </p>
                  {availableEventTypes.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {availableEventTypes.map((eventType) => (
                        <div
                          key={eventType}
                          className="rounded-md border border-gray-200 bg-gray-50 p-2"
                        >
                          <div className="flex items-center justify-between">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="flex-1 cursor-help text-xs">
                                    {getEventTypeLabel(eventType)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {getEventTypeDescription(eventType)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              aria-label="Agregar evento"
                              onClick={() => handleAddEventType(eventType)}
                            >
                              <Plus className="size-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs italic text-gray-500">
                      Todos los tipos de eventos han sido agregados
                    </p>
                  )}
                </div>

                {eventMappings.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-gray-600">
                      Acciones de Salesforce
                    </p>
                    <div className="flex flex-col gap-3">
                      {eventMappings.map((mapping) => {
                        const hasUpdateLead = !!mapping.updateLead;

                        return (
                          <div
                            key={mapping.event}
                            className="rounded-md border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-xs font-semibold text-blue-600">
                                        {getEventTypeLabel(mapping.event)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {getEventTypeDescription(mapping.event)}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-6 text-red-600 hover:text-red-700"
                                  aria-label="Eliminar evento"
                                  onClick={() => handleRemoveEventType(mapping.event)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                              <Separator />
                              <div className="flex flex-col gap-2">
                                <div className={`flex items-center gap-2 ${hasUpdateLead ? "mb-2" : ""}`}>
                                  <Checkbox
                                    checked={hasUpdateLead}
                                    onCheckedChange={(checked) =>
                                      handleToggleUpdateLead(mapping.event, checked as boolean)
                                    }
                                  />
                                  <label className="text-xs">Actualizar Contacto</label>
                                </div>
                                {hasUpdateLead && (
                                  <div className="ml-6 mt-2">
                                    <div className="flex flex-col gap-3">
                                      <div className="flex flex-col gap-2">
                                        <Label className="text-xs">
                                          Object API Name <span className="text-red-500">*</span>
                                        </Label>
                                        <div className="flex items-center gap-2">
                                          <Input
                                            className="flex-1 bg-white text-sm"
                                            placeholder="Ej: Lead, Contact, Account"
                                            value={objectApiNamesByEvent[mapping.event] || ""}
                                            onChange={(e) =>
                                              handleObjectApiNameChange(
                                                mapping.event,
                                                e.target.value
                                              )
                                            }
                                          />
                                          <Button
                                            size="sm"
                                            onClick={() => handleLoadFields(mapping.event)}
                                            disabled={
                                              !!(
                                                objectApiNamesByEvent[mapping.event] &&
                                                loadingFields[objectApiNamesByEvent[mapping.event]]
                                              ) ||
                                              !objectApiNamesByEvent[mapping.event] ||
                                              objectApiNamesByEvent[mapping.event]?.trim() === ""
                                            }
                                          >
                                            Cargar campos
                                          </Button>
                                        </div>
                                      </div>

                                      {objectApiNamesByEvent[mapping.event] &&
                                        loadingFields[objectApiNamesByEvent[mapping.event]] && (
                                          <div className="flex items-center gap-2">
                                            <Spinner size="sm" />
                                            <p className="text-xs text-gray-500">
                                              Cargando campos...
                                            </p>
                                          </div>
                                        )}

                                      {objectApiNamesByEvent[mapping.event] &&
                                        fieldsByObject[objectApiNamesByEvent[mapping.event]] && (
                                          <div>
                                            <p className="mb-2 text-xs font-semibold text-gray-600">
                                              Selecciona los campos a actualizar (
                                              {
                                                fieldsByObject[objectApiNamesByEvent[mapping.event]]
                                                  .length
                                              }
                                              )
                                            </p>
                                            <div className="max-h-[400px] overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3">
                                              <div className="flex flex-col gap-4">
                                                {fieldsByObject[
                                                  objectApiNamesByEvent[mapping.event]
                                                ].map((field) => {
                                                  const isSelected =
                                                    selectedFieldsByEvent[mapping.event]?.[
                                                      field.name
                                                    ] !== undefined;
                                                  const fieldValue =
                                                    selectedFieldsByEvent[mapping.event]?.[
                                                      field.name
                                                    ] || "";

                                                  return (
                                                    <div
                                                      key={field.name}
                                                      className={`rounded-md border bg-white p-2 ${
                                                        isSelected
                                                          ? "border-blue-300"
                                                          : "border-gray-200"
                                                      }`}
                                                    >
                                                      <div className="flex flex-col gap-2">
                                                        <div className="flex items-center gap-2">
                                                          <Checkbox
                                                            checked={isSelected}
                                                            onCheckedChange={(checked) =>
                                                              handleToggleFieldSelection(
                                                                mapping.event,
                                                                field.name,
                                                                checked as boolean
                                                              )
                                                            }
                                                          />
                                                          <label className="text-xs font-semibold">
                                                            {field.name}
                                                          </label>
                                                          <span className="text-xs text-gray-500">
                                                            ({field.label})
                                                          </span>
                                                          {field.required && (
                                                            <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">
                                                              Requerido
                                                            </Badge>
                                                          )}
                                                        </div>
                                                        {isSelected && (
                                                          <div className="ml-6">
                                                            {field.type === "picklist" &&
                                                            field.picklistValues ? (
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Valor
                                                                </Label>
                                                                <select
                                                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                  value={fieldValue}
                                                                  onChange={(e) =>
                                                                    handleFieldValueChange(
                                                                      mapping.event,
                                                                      field.name,
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                >
                                                                  <option value="">
                                                                    Selecciona un valor
                                                                  </option>
                                                                  {field.picklistValues.map(
                                                                    (picklistValue) => (
                                                                      <option
                                                                        key={picklistValue.value}
                                                                        value={picklistValue.value}
                                                                      >
                                                                        {picklistValue.label}
                                                                      </option>
                                                                    )
                                                                  )}
                                                                </select>
                                                              </div>
                                                            ) : (
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Valor
                                                                </Label>
                                                                <Input
                                                                  className="bg-white text-sm"
                                                                  value={fieldValue}
                                                                  onChange={(e) =>
                                                                    handleFieldValueChange(
                                                                      mapping.event,
                                                                      field.name,
                                                                      e.target.value
                                                                    )
                                                                  }
                                                                  placeholder={`Ingresa el valor para ${field.label}`}
                                                                />
                                                                <div className="mt-1 flex items-center">
                                                                  <VariableHelpTooltip />
                                                                </div>
                                                              </div>
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={handleSave}
                disabled={createConfiguration.isLoading || updateConfiguration.isLoading}
              >
                Guardar configuración
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-dashed border-gray-200 p-4">
            <p className="text-sm text-gray-600">
              Selecciona un tipo de negocio para comenzar a configurar el mapeo de eventos de
              Salesforce.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesforceCrmConfiguration;
